import { FormSpy, useForm, useFormState } from 'react-final-form';
import isEqual from 'react-fast-compare';
import React, { useEffect, useRef } from 'react';

type Props = {
  debounce?: number;
  onSubmit?: () => void;
};

export const DEFAULT_DEBOUNCE = 500;

const AutoSubmit: React.FC<Props> = ({
  debounce = DEFAULT_DEBOUNCE,
  onSubmit,
}) => {
  const form = useForm();
  const { values, initialValues, dirty } = useFormState();
  const prevInitialValues = useRef(initialValues);
  const submittingRef = useRef(false);
  const pendingValuesRef = useRef<any>(null);
  const submittedValuesRef = useRef<any>(initialValues);

  // Submit function that tracks what's being submitted
  const submitForm = async (valuesToSubmit: any) => {
    // Don't submit if values haven't changed from what we last submitted
    if (isEqual(valuesToSubmit, submittedValuesRef.current)) {
      return;
    }

    try {
      submittingRef.current = true;

      // Execute the submission
      if (onSubmit) {
        await onSubmit();
      } else {
        await form.submit();
      }

      // Track what we successfully submitted
      submittedValuesRef.current = valuesToSubmit;

      // Only update initialValues if the current form values match what we just submitted
      // This prevents overwriting user input that happened during submission
      const currentFormValues = form.getState().values;
      if (isEqual(currentFormValues, valuesToSubmit)) {
        form.initialize(valuesToSubmit);
      }
    } catch (error) {
      // Error during auto-submit
    } finally {
      submittingRef.current = false;

      // Check if there are newer values to submit
      if (
        pendingValuesRef.current &&
        !isEqual(pendingValuesRef.current, valuesToSubmit)
      ) {
        const nextValues = pendingValuesRef.current;
        pendingValuesRef.current = null;
        setTimeout(() => submitForm(nextValues), 0);
      }
    }
  };

  // Effect to handle form value changes
  useEffect(() => {
    // Check if initialValues have changed (e.g., from another user)
    const initialValuesChanged = !isEqual(
      initialValues,
      prevInitialValues.current,
    );
    prevInitialValues.current = initialValues;

    // Don't submit if initialValues just changed
    if (initialValuesChanged) {
      // Update our submitted values reference when initialValues change externally
      submittedValuesRef.current = initialValues;
      return undefined;
    }

    // Schedule submission after debounce
    if (dirty && !isEqual(values, initialValues)) {
      const timeout = window.setTimeout(() => {
        // If already submitting, just update pending values
        if (submittingRef.current) {
          pendingValuesRef.current = { ...values };
        } else {
          // Submit current values
          submitForm({ ...values });
        }
      }, debounce);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [values, initialValues, dirty, debounce]);

  return null;
};

const AutoSubmitForm: React.FC<Props> = props => (
  <FormSpy subscription={{ values: true }}>
    {() => <AutoSubmit {...props} />}
  </FormSpy>
);

export default AutoSubmitForm;
